import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type CookieSettings = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const Toggle = ({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange?: () => void;
  disabled?: boolean;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-label={label}
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`grid h-5 w-5 shrink-0 place-items-center border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a26850] focus-visible:ring-offset-2 ${
      checked ? "border-[#a26850] bg-[#a26850]" : "border-[#DCCFBF] bg-[#FBF8F3]"
    } ${disabled ? "cursor-not-allowed" : ""}`}
  >
    {checked && <span className="h-2 w-2 bg-[#F8F5EF]" />}
  </button>
);

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const language = i18n.language?.startsWith("fr")
    ? "fr"
    : i18n.language?.startsWith("es")
    ? "es"
    : "en";

  const copy = {
    fr: {
      label: "Vos préférences",
      title: "Confidentialité & cookies",
      description:
        "Nous utilisons des cookies pour améliorer votre expérience et mieux comprendre l’utilisation du site.",
      policy: "Politique de confidentialité",
      accept: "Tout accepter",
      necessaryOnly: "Essentiels uniquement",
      manage: "Personnaliser",
      settingsTitle: "Préférences de confidentialité",
      settingsIntro: "Choisissez les cookies que vous souhaitez autoriser.",
      necessary: "Strictement nécessaires",
      necessaryText: "Indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.",
      analytics: "Mesure d’audience",
      analyticsText: "Nous aide à comprendre la fréquentation du site et à améliorer votre expérience.",
      marketing: "Marketing",
      marketingText: "Permet de proposer des contenus et communications plus pertinents.",
      save: "Enregistrer mes choix",
      close: "Fermer les préférences",
    },
    es: {
      label: "Sus preferencias",
      title: "Privacidad y cookies",
      description:
        "Utilizamos cookies para mejorar su experiencia y comprender mejor el uso del sitio.",
      policy: "Política de privacidad",
      accept: "Aceptar todas",
      necessaryOnly: "Solo esenciales",
      manage: "Personalizar",
      settingsTitle: "Preferencias de privacidad",
      settingsIntro: "Elija las cookies que desea permitir.",
      necessary: "Estrictamente necesarias",
      necessaryText: "Imprescindibles para el funcionamiento del sitio. No se pueden desactivar.",
      analytics: "Medición de audiencia",
      analyticsText: "Nos ayuda a entender el tráfico y a mejorar su experiencia.",
      marketing: "Marketing",
      marketingText: "Permite ofrecer contenidos y comunicaciones más relevantes.",
      save: "Guardar mis preferencias",
      close: "Cerrar preferencias",
    },
    en: {
      label: "Your preferences",
      title: "Privacy & cookies",
      description:
        "We use cookies to improve your experience and better understand how the website is used.",
      policy: "Privacy policy",
      accept: "Accept all",
      necessaryOnly: "Essentials only",
      manage: "Customize",
      settingsTitle: "Privacy preferences",
      settingsIntro: "Choose which cookies you would like to allow.",
      necessary: "Strictly necessary",
      necessaryText: "Required for the website to work. They cannot be disabled.",
      analytics: "Audience measurement",
      analyticsText: "Helps us understand website traffic and improve your experience.",
      marketing: "Marketing",
      marketingText: "Enables more relevant content and communications.",
      save: "Save my choices",
      close: "Close preferences",
    },
  }[language];

  const readSettings = () => {
    const saved = localStorage.getItem("dem_cookie_settings");
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      setSettings({
        necessary: true,
        analytics: Boolean(parsed?.analytics),
        marketing: Boolean(parsed?.marketing),
      });
    } catch (error) {
      console.error("Failed to parse cookie settings", error);
    }
  };

  useEffect(() => {
    readSettings();
    if (!localStorage.getItem("dem_cookie_consent")) {
      const timer = window.setTimeout(() => setIsVisible(true), 1200);
      return () => window.clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const openSettings = () => {
      readSettings();
      setIsVisible(true);
      setShowSettings(true);
    };
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, []);

  useEffect(() => {
    if (!showSettings) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowSettings(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showSettings]);

  const close = () => {
    setIsVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const next: CookieSettings = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem("dem_cookie_consent", "all");
    localStorage.setItem("dem_cookie_settings", JSON.stringify(next));
    setSettings(next);
    close();
  };

  const acceptNecessary = () => {
    const next: CookieSettings = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem("dem_cookie_consent", "necessary_only");
    localStorage.setItem("dem_cookie_settings", JSON.stringify(next));
    setSettings(next);
    close();
  };

  const saveSettings = () => {
    localStorage.setItem("dem_cookie_consent", "custom");
    localStorage.setItem("dem_cookie_settings", JSON.stringify(settings));
    close();
  };

  const cookieRows = [
    {
      key: "necessary" as const,
      title: copy.necessary,
      description: copy.necessaryText,
      disabled: true,
    },
    {
      key: "analytics" as const,
      title: copy.analytics,
      description: copy.analyticsText,
    },
    {
      key: "marketing" as const,
      title: copy.marketing,
      description: copy.marketingText,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {showSettings ? (
            <motion.div
              key="settings"
              className="fixed inset-0 z-[110] flex items-end justify-center bg-[#100e0c]/45 p-4 sm:items-center sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-settings-title"
              onClick={() => setShowSettings(false)}
            >
              <motion.div
                className="w-full max-w-xl bg-[#F8F5EF] px-6 py-7 sm:px-8 sm:py-8"
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 16, opacity: 0 }}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <span className="font-oswald text-[11px] uppercase tracking-[0.34em] text-[#86523d]">
                      {copy.label}
                    </span>
                    <h2
                      id="cookie-settings-title"
                      className="mt-3 font-playfair text-3xl leading-tight text-stone-900"
                    >
                      {copy.settingsTitle}
                    </h2>
                    <p className="mt-3 font-playfair text-base leading-relaxed text-stone-600">
                      {copy.settingsIntro}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowSettings(false)}
                    aria-label={copy.close}
                    className="font-oswald text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors hover:text-[#86523d]"
                  >
                    {copy.close}
                  </button>
                </div>

                <div className="mt-8 space-y-6">
                  {cookieRows.map((row) => (
                    <div key={row.key} className="flex items-start justify-between gap-6">
                      <div>
                        <h3 className="font-oswald text-sm uppercase tracking-[0.14em] text-stone-900">
                          {row.title}
                        </h3>
                        <p className="mt-2 font-playfair text-base leading-relaxed text-stone-600">
                          {row.description}
                        </p>
                      </div>
                      <Toggle
                        checked={settings[row.key]}
                        disabled={row.disabled}
                        label={row.title}
                        onChange={() =>
                          setSettings((current) => ({
                            ...current,
                            [row.key]: !current[row.key],
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={saveSettings}
                  className="mt-8 inline-flex h-14 w-full items-center justify-center bg-[#171310] font-oswald text-xs uppercase tracking-[0.24em] text-[#F8F5EF] transition-colors hover:bg-[#a26850] sm:w-auto sm:px-10"
                >
                  {copy.save}
                </button>
              </motion.div>
            </motion.div>
          ) : (
            <motion.aside
              key="banner"
              className="fixed bottom-28 left-4 right-4 z-[100] max-w-md bg-[#F8F5EF] px-6 py-6 shadow-[0_16px_40px_rgba(16,14,12,0.16)] sm:bottom-6 sm:left-6"
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 16, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              aria-label={copy.title}
            >
              <span className="font-oswald text-[11px] uppercase tracking-[0.34em] text-[#86523d]">
                {copy.label}
              </span>
              <h2 className="mt-3 font-playfair text-2xl leading-tight text-stone-900">
                {copy.title}
              </h2>
              <p className="mt-3 font-playfair text-base leading-relaxed text-stone-600">
                {copy.description}{" "}
                <Link
                  to="/privacy-policy"
                  className="underline decoration-[#DCCFBF] underline-offset-4 transition-colors hover:text-[#86523d] hover:decoration-[#a26850]"
                >
                  {copy.policy}
                </Link>
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={acceptAll}
                  className="h-12 bg-[#171310] font-oswald text-xs uppercase tracking-[0.22em] text-[#F8F5EF] transition-colors hover:bg-[#a26850]"
                >
                  {copy.accept}
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={acceptNecessary}
                    className="h-12 border border-[#DCCFBF] font-oswald text-[11px] uppercase tracking-[0.14em] text-stone-800 transition-colors hover:border-[#a26850] hover:text-[#86523d]"
                  >
                    {copy.necessaryOnly}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className="h-12 border border-[#DCCFBF] font-oswald text-[11px] uppercase tracking-[0.14em] text-stone-800 transition-colors hover:border-[#a26850] hover:text-[#86523d]"
                  >
                    {copy.manage}
                  </button>
                </div>
              </div>
            </motion.aside>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
